import React, { useContext, useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, Popconfirm, Space, Spin } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  MONTH_NAMES,
  UtilityAccount,
  UtilityBill,
} from '@totem/components/portfolio/buildings/details/utilities/types';
import UtilityBillModal from '@totem/components/portfolio/buildings/details/utilities/UtilityBillModal';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const formatCurrency = (val: number) =>
  val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

type Props = {
  open: boolean;
  buildingId: string;
  account: UtilityAccount;
  onClose: () => void;
};

const UtilityBillsDrawer = ({ open, buildingId, account, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [bills, setBills] = useState<UtilityBill[]>([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editBill, setEditBill] = useState<UtilityBill>(null);

  useEffect(() => {
    if (open && account) {
      setRefresh(true);
    }
  }, [open, account]);

  useEffect(() => {
    if (!refresh || !account) {
      return;
    }

    setRefresh(false);

    setLoading(true);

    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-accounts/${account.id}/bills`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: UtilityBill[]) => {
        setBills(Array.isArray(result) ? result : []);
      })
      .finally(() => setLoading(false));
  }, [refresh, buildingId, account, getApiToken]);

  const handleDelete = (bill: UtilityBill) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-bills/${bill.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      setRefresh(true);
    });
  };

  const columns: ColumnProps<UtilityBill>[] = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 70,
    },
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 100,
      render: (_, record) => MONTH_NAMES[record.month - 1],
    },
    {
      title: 'kWh',
      dataIndex: 'kwh',
      key: 'kwh',
      align: 'right' as const,
      render: (_, record) => record.kwh.toLocaleString(),
    },
    {
      title: 'Energy Cost',
      dataIndex: 'energyCost',
      key: 'energyCost',
      align: 'right' as const,
      render: (_, record) => formatCurrency(record.energyCost),
    },
    {
      title: 'Peak kW',
      dataIndex: 'peakKw',
      key: 'peakKw',
      align: 'right' as const,
      render: (_, record) => record.peakKw.toLocaleString(),
    },
    {
      title: 'Demand Cost',
      dataIndex: 'demandCost',
      key: 'demandCost',
      align: 'right' as const,
      render: (_, record) => formatCurrency(record.demandCost),
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right' as const,
      render: (_, record) => formatCurrency(record.totalCost),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditBill(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this bill?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={`Bills — ${account?.label ?? ''}`}
        width={860}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditBill(null);

              setModalOpen(true);
            }}
          >
            Add Bill
          </Button>
        }
      >
        <Spin spinning={loading}>
          <IBTable
            columns={columns}
            dataSource={bills}
            rowKey={(record) => record.id}
            pagination={{ showSizeChanger: true, pageSize: 24 }}
            size="small"
          />
        </Spin>
      </Drawer>

      <UtilityBillModal
        open={modalOpen}
        buildingId={buildingId}
        accountId={account?.id ?? ''}
        bill={editBill}
        onClose={() => {
          setModalOpen(false);

          setEditBill(null);
        }}
        onSaved={() => setRefresh(true)}
      />
    </>
  );
};

export default UtilityBillsDrawer;
